/**
 * InlineSourceDetailPanel — Painel rico de detalhes para Ação/Problema
 * exibido inline na Zona 2 da Minha Mesa (Prompt 33 + Prompt 34).
 *
 * Reaproveita a estrutura visual do `ActionDetailView` da Central de Ações:
 * Header compacto (← Voltar · #ID · Status · Título · Responsável · Prazo · Concluir)
 * + grid 3 colunas: Contexto | Atividade/Histórico | Status/Resultados/Arquivos.
 *
 * Layout 100% fluido (w-full h-full overflow-y-auto), sem larguras fixas,
 * para se adaptar ao limite do Right Pane (~65% da tela).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Calendar,
  Check,
  CheckCircle2,
  Circle,
  Clock,
  DollarSign,
  ListChecks,
  Loader2,
  Lock,
  Paperclip,
  Send,
  Target,
  Upload,
  User,
} from 'lucide-react';
import type { UnifiedWorkItem } from '../types/workItem';
import { ActionService, type EnterpriseAction, type ActionStatus } from '../../../services/actionService';
import { IssueService } from '../../../services/issueService';
import { normalizeRequiredReason } from '../../../utils/problemReason';
import { ProblemReasonModal } from '../../../components/problemas/ProblemReasonModal';
import type { Issue, IssueStatus } from '../../../types/issue';
import { useAppToast } from '../../../components/AppToast';
import { useAuth } from '../../../context/AuthContext';
import { deskPriorityView } from '../../../components/ops/exceptionLoop';
import { OperationalOriginBanner } from '../../../components/problemas/OperationalOriginBanner';

interface InlineSourceDetailPanelProps {
  item: UnifiedWorkItem;
  organizationId: string | null;
  onBack: () => void;
  onCompleted: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────
function formatDateBR(iso?: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR');
  } catch {
    return '';
  }
}

function formatDateTimeBR(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

interface HeaderTheme {
  bg: string;
  border: string;
  text: string;
  tag: string;
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}

function getHeaderTheme(status: string, overdue: boolean): HeaderTheme {
  if (status === 'completed' || status === 'closed' || status === 'done') {
    return {
      bg: 'bg-emerald-50 dark:bg-emerald-500/10',
      border: 'border-emerald-200 dark:border-emerald-800/40',
      text: 'Concluída',
      tag: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-800/40',
      Icon: CheckCircle2,
    };
  }
  if (status === 'in_progress' || status === 'doing' || status === 'investigating' || status === 'analyzing') {
    return {
      bg: 'bg-blue-50 dark:bg-blue-500/10',
      border: 'border-blue-200 dark:border-blue-800/40',
      text: 'Em Andamento',
      tag: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-800/40',
      Icon: Clock,
    };
  }
  if (overdue) {
    return {
      bg: 'bg-red-50 dark:bg-red-500/10',
      border: 'border-red-200 dark:border-red-800/40',
      text: 'Atrasada',
      tag: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-800/40',
      Icon: AlertCircle,
    };
  }
  return { bg: 'bg-[var(--bg-canvas)]', border: 'border-[var(--border-default)]', text: 'A Fazer', tag: 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] border-[var(--border-default)]', Icon: Circle };
}

interface HydratedRecord {
  kind: 'action' | 'problem';
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  due_date: string | null;
  completed_at?: string | null;
  created_at: string;
  created_by: string | null;
  assigned_to: string | null;
  metadata: Record<string, unknown>;
  checklist: { id: string; text: string; checked: boolean }[];
  comments: { id: string; text: string; author: string; createdAt: string }[];
  attachments: { id: string; name: string; size?: string; type?: string; createdAt: string }[];
  context: { problem: string; plan: string };
  cost: number;
  issue_source?: string | null;
  issue_project_id?: string | null;
}

// ─────────────────────────────────────────────────────────────
export const InlineSourceDetailPanel: React.FC<InlineSourceDetailPanelProps> = ({
  item,
  organizationId,
  onBack,
  onCompleted,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast, ToastContainer } = useAppToast();
  const { user } = useAuth();
  const isProblem = item.source === 'problem';
  const [reasonAsk, setReasonAsk] = useState<null | 'close' | 'reopen'>(null);

  // Fetch full record from backend
  const recordQuery = useQuery<HydratedRecord | null>({
    queryKey: ['minha-mesa', 'source-detail', item.source, item.source_id],
    queryFn: async () => {
      if (isProblem) {
        if (!organizationId) return null;
        const issue = await IssueService.getById(organizationId, item.source_id);
        if (!issue) return null;
        const md = (((issue as unknown as { metadata?: unknown }).metadata) ?? {}) as Record<string, unknown>;
        return {
          kind: 'problem',
          id: issue.id,
          title: issue.title,
          description: issue.description ?? null,
          status: issue.status,
          priority: issue.severity ?? null,
          due_date: null,
          completed_at: null,
          created_at: issue.created_at,
          created_by: issue.created_by ?? null,
          assigned_to: issue.assigned_to ?? null,
          metadata: md,
          checklist: (md.checklist as HydratedRecord['checklist']) ?? [],
          comments: (md.comments as HydratedRecord['comments']) ?? [],
          attachments: (md.attachments as HydratedRecord['attachments']) ?? [],
          context: {
            problem: (md.context_problem as string) ?? issue.description ?? '',
            plan: (md.context_plan as string) ?? '',
          },
          cost: Number(md.cost ?? 0),
          issue_source: issue.source ?? null,
          issue_project_id: issue.project_id ?? null,
        } satisfies HydratedRecord;
      }
      const action: EnterpriseAction | null = await ActionService.getById(item.source_id);
      if (!action) return null;
      const md = (action.metadata ?? {}) as Record<string, unknown>;
      return {
        kind: 'action',
        id: action.id,
        title: action.title,
        description: action.description ?? null,
        status: action.status,
        priority: action.priority,
        due_date: action.due_date,
        completed_at: action.completed_at,
        created_at: action.created_at,
        created_by: action.created_by ?? null,
        assigned_to: action.assigned_to,
        metadata: md,
        checklist: (md.checklist as HydratedRecord['checklist']) ?? [],
        comments: (md.comments as HydratedRecord['comments']) ?? [],
        attachments: (md.attachments as HydratedRecord['attachments']) ?? [],
        context: {
          problem: (md.context_problem as string) ?? '',
          plan: (md.context_plan as string) ?? '',
        },
        cost: Number((md.cost as number) ?? 0),
      } satisfies HydratedRecord;
    },
    enabled: Boolean(item.source_id) && (!isProblem || Boolean(organizationId)),
    staleTime: 30_000,
  });

  const record = recordQuery.data ?? null;

  // ─── Local form state (optimistic) ─────────────────────────
  const [localTitle, setLocalTitle] = useState(item.title);
  const [contextProblem, setContextProblem] = useState('');
  const [contextPlan, setContextPlan] = useState('');
  const [checklistInput, setChecklistInput] = useState('');
  const [commentInput, setCommentInput] = useState('');
  const [cost, setCost] = useState(0);
  const [persisting, setPersisting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (record) {
      setLocalTitle(record.title);
      setContextProblem(record.context.problem);
      setContextPlan(record.context.plan);
      setCost(record.cost ?? 0);
    } else {
      setLocalTitle(item.title);
      setContextProblem('');
      setContextPlan('');
      setCost(0);
    }
  }, [record, item.title]);

  const checklist = record?.checklist ?? [];
  const comments = record?.comments ?? [];
  const attachments = record?.attachments ?? [];

  const progress = checklist.length > 0 ? Math.round((checklist.filter((i) => i.checked).length / checklist.length) * 100) : 0;
  const theme = getHeaderTheme(record?.status ?? item.status, item.is_overdue ?? false);

  // ─── Persist helpers ───────────────────────────────────────
  const persistMetadataPatch = useCallback(async (patch: Record<string, unknown>) => {
    if (!record) return;
    setPersisting(true);
    try {
      const nextMetadata = { ...(record.metadata ?? {}), ...patch } as Record<string, unknown>;
      if (record.kind === 'action') {
        await ActionService.update(record.id, { metadata: nextMetadata });
      } else {
        if (!organizationId) throw new Error('Organização ausente');
        await IssueService.update(organizationId, record.id, { metadata: nextMetadata } as unknown as Partial<Issue>);
      }
      await queryClient.invalidateQueries({ queryKey: ['minha-mesa', 'source-detail', item.source, item.source_id] });
      await queryClient.invalidateQueries({ queryKey: ['minha-mesa'] });
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Falha ao salvar');
    } finally {
      setPersisting(false);
    }
  }, [record, organizationId, queryClient, item.source, item.source_id, showToast]);

  const persistField = useCallback(async (field: 'title' | 'context_problem' | 'context_plan' | 'cost', value: string | number) => {
    if (!record) return;
    if (field === 'title') {
      setPersisting(true);
      try {
        if (record.kind === 'action') {
          await ActionService.update(record.id, { title: String(value) });
        } else if (organizationId) {
          await IssueService.update(organizationId, record.id, { title: String(value) } as unknown as Partial<Issue>);
        }
        await queryClient.invalidateQueries({ queryKey: ['minha-mesa', 'source-detail', item.source, item.source_id] });
      } catch (e) {
        showToast('error', e instanceof Error ? e.message : 'Falha ao salvar');
      } finally {
        setPersisting(false);
      }
      return;
    }
    await persistMetadataPatch({ [field]: value });
  }, [record, organizationId, queryClient, item.source, item.source_id, persistMetadataPatch, showToast]);

  const persistDueDate = useCallback(async (iso: string | null) => {
    if (!record || record.kind !== 'action') return;
    setPersisting(true);
    try {
      await ActionService.update(record.id, { due_date: iso });
      await queryClient.invalidateQueries({ queryKey: ['minha-mesa', 'source-detail', item.source, item.source_id] });
      await queryClient.invalidateQueries({ queryKey: ['minha-mesa'] });
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Falha ao salvar prazo');
    } finally {
      setPersisting(false);
    }
  }, [record, queryClient, item.source, item.source_id, showToast]);

  // ─── Checklist handlers ────────────────────────────────────
  const handleAddChecklist = useCallback(async () => {
    const text = checklistInput.trim();
    if (!text || !record) return;
    const next = [...checklist, { id: Date.now().toString(), text, checked: false }];
    setChecklistInput('');
    await persistMetadataPatch({ checklist: next });
  }, [checklistInput, checklist, record, persistMetadataPatch]);

  const handleToggleChecklist = useCallback(async (id: string) => {
    const next = checklist.map((c) => (c.id === id ? { ...c, checked: !c.checked } : c));
    await persistMetadataPatch({ checklist: next });
  }, [checklist, persistMetadataPatch]);

  // ─── Comment handlers ──────────────────────────────────────
  const handleAddComment = useCallback(async () => {
    const text = commentInput.trim();
    if (!text || !record) return;
    const authorName = user?.name || user?.email || 'Eu';
    const next = [...comments, { id: Date.now().toString(), text, author: authorName, createdAt: new Date().toISOString() }];
    setCommentInput('');
    await persistMetadataPatch({ comments: next });
  }, [commentInput, comments, record, user, persistMetadataPatch]);

  // ─── Attachment handlers ───────────────────────────────────
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !record) return;
    const next = [
      ...attachments,
      { id: Date.now().toString(), name: file.name, size: (file.size / 1024).toFixed(1) + ' KB', type: file.type, createdAt: new Date().toISOString() },
    ];
    e.target.value = '';
    await persistMetadataPatch({ attachments: next });
  }, [attachments, record, persistMetadataPatch]);

  // ─── Conclude ──────────────────────────────────────────────
  const handleComplete = useCallback(async () => {
    if (!record || completing) return;
    setCompleting(true);
    try {
      if (record.kind === 'action') {
        const nextStatus: ActionStatus = record.status === 'completed' ? 'pending' : 'completed';
        await ActionService.update(record.id, { status: nextStatus });
      } else {
        if (!organizationId) throw new Error('Organização ausente');
        const nextStatus: IssueStatus = (record.status === 'closed' ? 'open' : 'closed') as IssueStatus;
        if (nextStatus === 'open') {
          setReasonAsk('reopen');
          return;
        }
        const stored = normalizeRequiredReason(typeof record.metadata?.closure_reason === 'string' ? record.metadata.closure_reason : null);
        if (!stored) {
          setReasonAsk('close');
          return;
        }
        await IssueService.transitionStatus(organizationId, record.id, 'closed');
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['minha-mesa'] }),
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['execution'] }),
        queryClient.invalidateQueries({ queryKey: ['minha-mesa', 'source-detail', item.source, item.source_id] }),
      ]);
      onCompleted();
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Falha ao concluir');
    } finally {
      setCompleting(false);
    }
  }, [record, organizationId, completing, queryClient, item.source, item.source_id, onCompleted, showToast]);

  const confirmReasonAsk = useCallback(async (raw: string) => {
    const reason = normalizeRequiredReason(raw);
    if (!reason || !reasonAsk || !record || !organizationId) return;
    const mode = reasonAsk;
    setReasonAsk(null);
    setCompleting(true);
    try {
      const nextStatus: IssueStatus = mode === 'reopen' ? 'open' : 'closed';
      await IssueService.transitionStatus(organizationId, record.id, nextStatus, mode === 'reopen'
        ? { reopenReason: reason }
        : { closureReason: reason });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['minha-mesa'] }),
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['execution'] }),
        queryClient.invalidateQueries({ queryKey: ['minha-mesa', 'source-detail', item.source, item.source_id] }),
      ]);
      onCompleted();
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Falha ao concluir');
    } finally {
      setCompleting(false);
    }
  }, [reasonAsk, record, organizationId, queryClient, item.source, item.source_id, onCompleted, showToast]);

  const idTail = useMemo(() => item.source_id.slice(-4).toLowerCase(), [item.source_id]);

  // ─── Governança de edição (Poka-Yoke) ──────────────────────
  // - isLocked: status terminal (completed/closed/done) → tudo Read-Only.
  // - isOwner: usuário atual é Criador/Gestor → único autorizado a alterar metadados.
  // - canEditMetadata: título, contexto, prazo (somente Criador/Gestor e ativo).
  // - canEditOperational: checklist, custo, comentários, anexos (qualquer ativo).
  const status = record?.status ?? item.status;
  const isCompleted = status === 'completed' || status === 'closed' || status === 'done';
  const isLocked = isCompleted;
  const currentUserId = user?.id ?? null;
  const isOwner = !!currentUserId && (!record?.created_by || record.created_by === currentUserId);
  const isActiveStatus = status === 'pending' || status === 'in_progress' || status === 'open' || status === 'investigating' || status === 'analyzing' || status === 'doing';
  const canEditMetadata = !isLocked && isOwner && isActiveStatus;
  const canEditOperational = !isLocked;

  const linkedProblemId = (
    (record?.metadata?.problem_id as string | undefined)
    ?? item.source_context?.problem_id
    ?? null
  );
  const priorityView = !isProblem
    ? deskPriorityView(record?.priority ?? item.source_context?.managerial_priority, item.source_context?.operational_signal)
    : null;

  if (recordQuery.isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[var(--bg-canvas)]">
        <Loader2 size={20} className="animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-[var(--bg-canvas)] font-sans text-[var(--text-primary)]">
      {!isProblem && (linkedProblemId || priorityView) ? (
        <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--border-default)] bg-violet-50/70 px-3 py-1.5 text-[11px] dark:bg-violet-950/20">
          <span className="font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">Ação</span>
          {priorityView ? (
            <span>Prioridade gerencial: {priorityView.managerialLabel}</span>
          ) : null}
          {priorityView?.operationalSignal ? (
            <span>Sinal operacional: {priorityView.operationalSignal}</span>
          ) : null}
          {linkedProblemId ? (
            <button
              type="button"
              onClick={() => navigate(`/problemas?id=${encodeURIComponent(linkedProblemId)}`)}
              className="font-semibold text-[var(--nav-active-bar)]"
            >
              Abrir problema
            </button>
          ) : null}
        </div>
      ) : null}
      {isProblem && record ? (
        <OperationalOriginBanner
          metadata={record.metadata}
          source={record.issue_source}
          projectId={record.issue_project_id}
          organizationId={organizationId}
        />
      ) : null}
      {/* HEADER COMPACTO — permite título quebrar em até 2 linhas */}
      <div className={`flex min-h-12 shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b px-3 py-1.5 bg-[var(--bg-canvas)] ${theme.border}`}>
        <button
          onClick={onBack}
          className="p-1 text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]"
          title="Voltar"
        >
          <ArrowLeft size={16} />
        </button>
        <div className={`flex items-center gap-1.5 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${theme.tag}`}>
          <theme.Icon size={10} strokeWidth={3} />
          {theme.text}
        </div>
        <span className="font-mono text-[10px] text-[var(--text-tertiary)]">#{idTail}</span>
        <div className="h-4 w-px bg-[var(--border-default)]" />
        {canEditMetadata ? (
          <textarea
            rows={1}
            className="line-clamp-2 min-h-[2rem] min-w-0 flex-1 resize-none overflow-hidden border border-transparent bg-transparent px-2 py-1 text-[15px] font-semibold leading-tight text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:border-[var(--border-subtle)] focus:bg-[var(--bg-surface)] focus:outline-none"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            onBlur={() => { if (localTitle !== record?.title) void persistField('title', localTitle); }}
            placeholder={isProblem ? 'Título do Problema' : 'Nome da Ação'}
          />
        ) : (
          <span
            className="flex min-w-0 flex-1 items-start gap-1.5 px-2 py-1 text-[15px] font-semibold leading-tight text-[var(--text-primary)]"
            title={isLocked ? 'Bloqueado: registro concluído' : 'Apenas o Criador/Gestor pode editar'}
          >
            {isLocked && <Lock size={11} className="mt-0.5 shrink-0 text-[var(--text-tertiary)]" />}
            <span className="line-clamp-2 break-words">{localTitle || (isProblem ? 'Título do Problema' : 'Nome da Ação')}</span>
          </span>
        )}

        {/* Responsável */}
        <div className="flex items-center gap-2 px-2 py-1">
          <div className="flex h-5 w-5 items-center justify-center bg-[var(--bg-surface-3)] text-[9px] font-bold text-[var(--text-primary)]">
            <User size={10} />
          </div>
          <span className="text-xs font-medium text-[var(--text-secondary)]">{user?.name || 'Eu'}</span>
        </div>

        {/* Prazo (apenas Ação) */}
        {!isProblem && (
          <div className="flex items-center gap-2 border border-transparent px-2 py-1 hover:border-[var(--border-default)] hover:bg-[var(--bg-elevated)]">
            <Calendar size={14} className={record?.due_date ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'} />
            {canEditMetadata ? (
              <input
                type="date"
                className="w-auto cursor-pointer border-none bg-transparent p-0 text-xs font-medium text-[var(--text-secondary)] focus:outline-none"
                value={record?.due_date ? new Date(record.due_date).toISOString().split('T')[0] : ''}
                onChange={(e) => void persistDueDate(e.target.value || null)}
              />
            ) : (
              <span className="text-xs font-medium text-[var(--text-secondary)]">
                {record?.due_date ? formatDateBR(record.due_date) : '—'}
              </span>
            )}
          </div>
        )}

        <div className="h-4 w-px bg-[var(--border-default)]" />

        <button
          onClick={() => void handleComplete()}
          disabled={completing}
          className={`flex items-center gap-1.5 border px-2.5 py-1 text-[10px] font-semibold transition-colors ${
            isCompleted
              ? 'bg-[var(--bg-surface)] text-emerald-600 border-[var(--border-default)] hover:bg-[var(--bg-elevated)]'
              : 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
          } disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {completing ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
          {isCompleted ? 'Reabrir' : 'Concluir'}
        </button>
      </div>

      {/* CORPO 2 COLUNAS — Prompt 36
          A (col-span-7): Contexto + Atividade verticalmente contínuos.
          B (col-span-5): Status · Datas · Checklist · Custo · Arquivos empilhados. */}
      <div className="grid min-h-0 w-full flex-1 grid-cols-12 gap-0 overflow-hidden">
        {/* ── SUB-COLUNA A · CONTEXTO + ATIVIDADE ── */}
        <section className="col-span-7 flex h-full min-h-0 flex-col overflow-hidden bg-[var(--bg-canvas)]">
          <div className="min-h-0 flex-1 overflow-y-auto">
            {/* Bloco Contexto */}
            <div className="border-b border-[var(--border-default)] px-5 py-4">
              <h3 className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--text-secondary)]">
                <Target size={11} /> Contexto
              </h3>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">O Problema</label>
              {canEditMetadata ? (
                <textarea
                  value={contextProblem}
                  onChange={(e) => setContextProblem(e.target.value)}
                  onBlur={() => { if (contextProblem !== (record?.context.problem ?? '')) void persistField('context_problem', contextProblem); }}
                  placeholder="Descreva..."
                  rows={3}
                  className="mb-3 w-full resize-none border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-2 text-[12px] leading-relaxed text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-[var(--nav-active-bar)] focus:outline-none"
                />
              ) : (
                <p className="mb-3 min-h-[3rem] w-full whitespace-pre-wrap border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-2 text-[12px] leading-relaxed text-[var(--text-secondary)]">
                  {contextProblem || <span className="text-[var(--text-tertiary)]">—</span>}
                </p>
              )}
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">O Plano</label>
              {canEditMetadata ? (
                <textarea
                  value={contextPlan}
                  onChange={(e) => setContextPlan(e.target.value)}
                  onBlur={() => { if (contextPlan !== (record?.context.plan ?? '')) void persistField('context_plan', contextPlan); }}
                  placeholder="Execução..."
                  rows={4}
                  className="w-full resize-none border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-2 text-[12px] leading-relaxed text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-[var(--nav-active-bar)] focus:outline-none"
                />
              ) : (
                <p className="min-h-[4rem] w-full whitespace-pre-wrap border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-2 text-[12px] leading-relaxed text-[var(--text-secondary)]">
                  {contextPlan || <span className="text-[var(--text-tertiary)]">—</span>}
                </p>
              )}
            </div>

            {/* Bloco Atividade */}
            <div className="px-5 py-4">
              <h3 className="mb-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--text-secondary)]">
                <Activity size={11} /> Atividade
              </h3>
              {comments.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-6 text-center text-[var(--text-tertiary)]">
                  <div className="flex h-10 w-10 items-center justify-center border border-[var(--border-default)] bg-[var(--bg-surface)]">
                    <Activity size={16} />
                  </div>
                  <p className="text-[11px]">Sem histórico ainda.</p>
                </div>
              ) : (
                <ul className="flex flex-col gap-3">
                  {comments.map((c) => (
                    <li key={c.id} className="flex gap-2">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center bg-[var(--bg-surface-3)] text-[10px] font-bold text-[var(--text-primary)]">
                        {c.author.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1 border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-3 py-2">
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <span className="text-[11px] font-semibold text-[var(--text-primary)]">{c.author}</span>
                          <span className="font-mono text-[9px] text-[var(--text-tertiary)]">{formatDateTimeBR(c.createdAt)}</span>
                        </div>
                        <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-[var(--text-secondary)]">{c.text}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Composer fixo no rodapé */}
          <div className="shrink-0 border-t border-[var(--border-default)] bg-[var(--bg-surface)] px-3 py-2">
            {canEditOperational ? (
              <div className="flex items-center gap-2">
                <input
                  value={commentInput}
                  onChange={(e) => setCommentInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleAddComment(); } }}
                  placeholder="Escreva um comentário..."
                  className="h-8 min-w-0 flex-1 border border-[var(--border-subtle)] bg-[var(--bg-canvas)] px-3 text-[12px] focus:border-[var(--nav-active-bar)] focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => void handleAddComment()}
                  disabled={!commentInput.trim()}
                  className="flex h-8 w-8 items-center justify-center border border-[var(--border-subtle)] bg-[var(--bg-canvas)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] disabled:cursor-not-allowed disabled:opacity-50"
                  title="Enviar"
                >
                  <Send size={12} />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 border border-[var(--border-subtle)] bg-[var(--bg-canvas)] px-3 py-2 text-[11px] font-medium text-[var(--text-tertiary)]">
                <Lock size={11} />
                <span>Esta ação foi concluída e está fechada para novas interações.</span>
              </div>
            )}
          </div>
        </section>

        {/* ── SUB-COLUNA B · CONTROLES EMPILHADOS ── */}
        <aside className="col-span-5 flex h-full min-h-0 flex-col gap-3 overflow-y-auto border-l border-[var(--border-default)] bg-[var(--bg-surface)] p-4">
          {/* Status */}
          <section>
            <h3 className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--text-secondary)]">Status</h3>
            <div className="border border-[var(--border-subtle)] bg-[var(--bg-canvas)] px-3 py-2 text-[12px] font-semibold text-[var(--text-primary)]">
              {theme.text}
            </div>
            {item.is_overdue && !isCompleted && (
              <div className="mt-2 flex items-center gap-1.5 border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] font-semibold text-red-700 dark:border-red-800/40 dark:bg-red-500/10 dark:text-red-300">
                <AlertCircle size={11} /> Atrasada
              </div>
            )}
          </section>

          {/* Datas */}
          <section>
            <h3 className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--text-secondary)]">
              <Calendar size={11} /> Datas
            </h3>
            <div className="border border-[var(--border-subtle)] bg-[var(--bg-canvas)] px-3 py-2 text-[11px] text-[var(--text-secondary)]">
              <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">Criado</span><span>{formatDateBR(record?.created_at ?? item.created_at)}</span></div>
              {record?.due_date && <div className="mt-1 flex justify-between"><span className="text-[var(--text-tertiary)]">Prazo</span><span>{formatDateBR(record.due_date)}</span></div>}
              {record?.completed_at && <div className="mt-1 flex justify-between"><span className="text-[var(--text-tertiary)]">Concluído</span><span>{formatDateBR(record.completed_at)}</span></div>}
            </div>
          </section>

          {/* Checklist */}
          <section>
            <h3 className="mb-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--text-secondary)]">
              <span className="flex items-center gap-1.5"><ListChecks size={11} /> Checklist</span>
              <span className="font-mono text-[10px] text-[var(--text-tertiary)]">{checklist.filter((c) => c.checked).length}/{checklist.length}</span>
            </h3>
            {checklist.length === 0 ? (
              <p className="border border-dashed border-[var(--border-default)] bg-[var(--bg-canvas)] py-3 text-center text-[11px] text-[var(--text-tertiary)]">Vazio</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {checklist.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => void handleToggleChecklist(c.id)}
                      disabled={!canEditOperational}
                      className="group flex w-full items-start gap-2 border border-transparent px-2 py-1.5 text-left text-[12px] enabled:hover:border-[var(--border-subtle)] enabled:hover:bg-[var(--bg-elevated)] disabled:cursor-not-allowed"
                    >
                      <span className={`mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center border ${c.checked ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-[var(--border-default)] bg-transparent'}`}>
                        {c.checked && <Check size={9} strokeWidth={3} />}
                      </span>
                      <span className={`min-w-0 flex-1 ${c.checked ? 'text-[var(--text-tertiary)] line-through' : 'text-[var(--text-primary)]'}`}>{c.text}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {progress > 0 && (
              <div className="mt-2 h-1 w-full overflow-hidden bg-[var(--bg-elevated)]">
                <div className="h-full bg-emerald-500" style={{ width: `${progress}%` }} />
              </div>
            )}
            <div className="mt-2 flex gap-1">
              <input
                value={checklistInput}
                onChange={(e) => setChecklistInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAddChecklist(); } }}
                disabled={!canEditOperational}
                placeholder={canEditOperational ? 'Nova etapa...' : 'Bloqueado'}
                className="h-7 min-w-0 flex-1 border border-[var(--border-subtle)] bg-[var(--bg-canvas)] px-2 text-[11px] focus:border-[var(--nav-active-bar)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => void handleAddChecklist()}
                disabled={!canEditOperational}
                className="h-7 w-7 shrink-0 border border-[var(--border-subtle)] bg-[var(--bg-canvas)] text-[var(--text-secondary)] enabled:hover:bg-[var(--bg-elevated)] disabled:cursor-not-allowed disabled:opacity-50"
                title="Adicionar"
              >
                +
              </button>
            </div>
          </section>

          {/* Custo Real */}
          <section>
            <h3 className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--text-secondary)]">
              <DollarSign size={11} /> Custo Real
            </h3>
            <div className={`flex items-center border border-[var(--border-subtle)] bg-[var(--bg-canvas)] ${!canEditOperational ? 'opacity-60' : ''}`}>
              <span className="px-2 font-mono text-[11px] text-[var(--text-tertiary)]">R$</span>
              <input
                type="number"
                value={cost}
                onChange={(e) => setCost(Number(e.target.value) || 0)}
                onBlur={() => { if (cost !== record?.cost) void persistField('cost', cost); }}
                disabled={!canEditOperational}
                step="0.01"
                min="0"
                className="h-8 min-w-0 flex-1 border-none bg-transparent px-1 text-right text-[12px] focus:outline-none disabled:cursor-not-allowed"
              />
            </div>
          </section>

          {/* Arquivos */}
          <section>
            <h3 className="mb-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--text-secondary)]">
              <span className="flex items-center gap-1.5"><Paperclip size={11} /> Arquivos</span>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canEditOperational}
                className="border border-[var(--border-subtle)] bg-[var(--bg-canvas)] px-2 py-0.5 text-[10px] font-semibold text-[var(--text-secondary)] enabled:hover:bg-[var(--bg-elevated)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                + Add
              </button>
            </h3>
            <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => void handleFileUpload(e)} />
            {attachments.length === 0 ? (
              canEditOperational ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex h-16 w-full flex-col items-center justify-center gap-1 border border-dashed border-[var(--border-default)] bg-[var(--bg-canvas)] text-[var(--text-tertiary)] hover:border-[var(--nav-active-bar)] hover:text-[var(--text-secondary)]"
                >
                  <Upload size={14} />
                  <span className="text-[11px]">Anexar</span>
                </button>
              ) : (
                <div className="flex h-16 w-full flex-col items-center justify-center gap-1 border border-dashed border-[var(--border-default)] bg-[var(--bg-canvas)] text-[var(--text-tertiary)]">
                  <Lock size={12} />
                  <span className="text-[11px]">Sem anexos</span>
                </div>
              )
            ) : (
              <ul className="flex flex-col gap-1">
                {attachments.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 border border-[var(--border-subtle)] bg-[var(--bg-canvas)] px-2 py-1.5 text-[11px]">
                    <Paperclip size={11} className="shrink-0 text-[var(--text-tertiary)]" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[var(--text-primary)]">{a.name}</div>
                      {a.size && <div className="font-mono text-[9px] text-[var(--text-tertiary)]">{a.size}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>

      {persisting && (
        <div className="pointer-events-none absolute bottom-3 right-4 flex items-center gap-1.5 border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 py-1 text-[10px] text-[var(--text-tertiary)] shadow">
          <Loader2 size={10} className="animate-spin" /> Salvando…
        </div>
      )}

      <ProblemReasonModal
        open={reasonAsk != null}
        mode={reasonAsk === 'reopen' ? 'reopen' : 'close'}
        onClose={() => setReasonAsk(null)}
        onConfirm={(reason) => { void confirmReasonAsk(reason); }}
      />
      <ToastContainer />
    </div>
  );
};

export default InlineSourceDetailPanel;
